import React, { useState, useEffect, useMemo, useCallback, useContext } from "react";
import { LoadingIndicator } from "components/framework/loadingIndicator/LoadingIndicator";
import { IntlShape, useIntl } from "react-intl";
import { getValueOrEmpty } from "services/util/StringUtil";
import ReactTable from "components/framework/table/ReactTable";
import { TradingPartnerActionCell } from "components/common/actions/TradingPartnerActionCell";
import { authorize } from "services/authorization/AuthorizationService";
import { PermissionType } from "services/authorization/PermissionType";
import { handleError } from "services/util/ApiUtil";
import { AppContext } from "services/appContext/AppContext";
import { useIsMounted } from "services/customHooks/useIsMounted";
import { TppListFilter, DefaultTppListFilter } from "services/apis/types/tpp/TppListFilter";
import { TableIdentifiers } from "services/uiSettings/TableIdentifiers";
import { TradingPartnerTableColumnIdentifiers } from "components/networkExplorer/types/TradingPartnerColumnIdentifier";
import { WebhookLogsEmailTableColumnIdentifiers } from "./types/WebhookLogsIdentifier";
import moment from "moment";
import { WebhookEmailStatus, buildQueryParams } from "services/util/WebhookLogsUtil";
import { WebhookLogsApi } from "services/apis/WebhookLogsApi";
import { EmailLogs } from "services/apis/types/webhookLogs/WebhookEmailLogsDto";
import FormatDateTime from "components/framework/date/FormatDateTime";

type ColumnFilterSort = {
  isSorted?: boolean;
  isSortedDesc?: boolean;
}

type Props = {
  search: string;
};

export default function EmailNotificationsTable(props: Props) {
  const [showLoadingIndicator, setShowLoadingIndicator] = useState(false);
  const [emailLogsDetails, setEmailLogsDetails] = useState<EmailLogs[]>();
  const [userIsAdmin] = useState(authorize(PermissionType.Admin));
  const { appContext } = useContext(AppContext);
  const isMounted = useIsMounted();
  const intl = useIntl();
  const [searchKey, setSearchKey] = useState("");
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [count, setCount] = useState(0);
  const [triggerRefresh, setTriggerRefresh] = useState(false);
  const [selectedFilter, setSelectedFilter] = useState<TppListFilter>(DefaultTppListFilter);
  const [isAppStartUp, setIsAppStartUp] = useState(false);
  const [orderByColumn, setOrderByColumn] = useState("");
  const [orderDirection, setOrderDirection] = useState("");
  const [columnFilterSort, setColumnFilterSort] = useState<{ [key: string]: ColumnFilterSort }>({});
  const [status, setStatus] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [orderId, setOrderId] = useState("");

  const resetAllFilters = useCallback(() => {
    if (isMounted.current) {
      setSelectedFilter(DefaultTppListFilter);
    }
  }, [isMounted]);

  const toggleRefresh = useCallback(() => {
    if (isMounted.current) {
      setTriggerRefresh((x) => !x);
    }
  }, [isMounted]);

  const areFiltersmodified = useCallback((): boolean => {
    const queryString = new URLSearchParams(
      Object.fromEntries(Object.entries(selectedFilter).filter(([_, v]) => v !== "" && v !== "Any"))
    ).toString();
    return queryString ? true : false;
  }, [selectedFilter]);

  const updateHiddenColumns = useCallback((columns) => {
    const excludedColumnsForWholesale = [TradingPartnerTableColumnIdentifiers.RequestingSpId];
    if (!userIsAdmin) {
      return columns.filter(column => column.identifier && !excludedColumnsForWholesale.includes(column.identifier))
    };
    return columns;
  }, [userIsAdmin]);

  const columnFilter = useCallback(
    (rows: any[], accessor: any, filterValues: any) => {
      if (accessor[0] === "requestingSpid") {
        if (selectedFilter["tenant"] !== filterValues)
          setSelectedFilter((prevState) => ({
            ...prevState,
            ...{
              tenant: filterValues
            }
          }));
      } else if (accessor[0] === "targetSpid") {
        if (selectedFilter["tradingSpid"] !== filterValues)
          setSelectedFilter((prevState) => ({
            ...prevState,
            ...{
              tradingSpid: filterValues
            }
          }));
      } else if (selectedFilter[accessor[0]] !== filterValues) {
        setSelectedFilter((prevState) => ({
          ...prevState,
          ...{
            [accessor[0]]: filterValues
          }
        }));
      }
      return rows;
    },
    [selectedFilter]
  );

  const handleSearch = (data) => {
    const formattedData = {
      orderId: data.orderIdValue ? data.orderIdValue : '',
      Status: data.statusValue ? data.statusValue : '',
      FromDate: data.fromDate ? data.fromDate : '',
      ToDate: data.toDate ? data.toDate : ''
    };
    setOrderId(formattedData.orderId);
    setStatus(formattedData.Status);
    setFromDate(formattedData.FromDate);
    setToDate(formattedData.ToDate);
  };

  const getEmailLogsList = (
    currentPage: number,
    pageSize: number,
    orderByColumn: string,
    orderDirection: string,
    filterValues: {
        orderId?: string,
        Status?: string,
        FromDate?: string,
        ToDate?: string
    },
    tenant: string | undefined = undefined
) => {
    const params = buildQueryParams({
        pageNumber: currentPage.toString(),
        pageSize: pageSize.toString(),
        orderByColumn: orderByColumn,
        orderDirection: orderDirection,
        orderId: filterValues.orderId,
        Status: filterValues.Status,
        FromDate: filterValues.FromDate,
        ToDate: filterValues.ToDate
    });

    WebhookLogsApi.getEmailLogsList(params, tenant)
        .then((result) => {
            if (isMounted && result) {
                if (result.rows) {
                    setEmailLogsDetails(result.rows);
                    setCount(result.totalRows);
                }
            }
        })
        .catch((error) => handleError(error))
        .finally(() => {
            if (isMounted) {
                setShowLoadingIndicator(false);
            }
        });
};

  useEffect(() => {
    if (isMounted.current && isAppStartUp) {
      setEmailLogsDetails(undefined);
      const trimmedOrderId = orderId.trim();
      const queryString = new URLSearchParams(
        Object.fromEntries(
          Object.entries(selectedFilter).filter(([_, v]) => v !== "" && v !== "Any")
        )
      ).toString();
      getEmailLogsList(pageIndex + 1, pageSize, orderByColumn, orderDirection, {
        orderId: trimmedOrderId,
        Status: status,
        FromDate: fromDate,
        ToDate: toDate
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMounted, pageIndex, pageSize, status, fromDate, toDate, orderId]);

  useEffect(() => {
    if (isMounted.current && isAppStartUp) {
      setEmailLogsDetails(undefined);
      if (pageIndex !== 0) setPageIndex(0);
      else {
        const queryString = new URLSearchParams(
          Object.fromEntries(
            Object.entries(selectedFilter).filter(([_, v]) => v !== "" && v !== "Any")
          )
        ).toString();
        getEmailLogsList(pageIndex + 1, pageSize, orderByColumn, orderDirection, {
          orderId: orderId,
          Status: status,
          FromDate: fromDate,
          ToDate: toDate
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMounted, selectedFilter]);

  const columns = useMemo(() => {
    const columnsList = getColumns(intl, columnFilter);
    return updateHiddenColumns(columnsList);
  }, [intl, updateHiddenColumns, columnFilter, toggleRefresh]);

  const handlePageChange = (pageIndex) => {
    setPageIndex(pageIndex);
  };

  const handlePageSizeChange = (event) => {
    setPageSize(event);
    setPageIndex(0);
  };

  useEffect(() => {
    if (triggerRefresh) {
      setSelectedFilter(DefaultTppListFilter);
      if (pageIndex !== 0) setPageIndex(0);
      setShowLoadingIndicator(true);
      setTriggerRefresh(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMounted, triggerRefresh]);

  useEffect(() => {
    if (isMounted.current) {
      setShowLoadingIndicator(true);
      setIsAppStartUp(true);
      setSearchKey("");
      if (pageIndex !== 0 || pageSize !== 10 || areFiltersmodified()) {
        if (pageIndex !== 0) setPageIndex(0);
        if (pageSize !== 10) setPageSize(10);
        if (areFiltersmodified()) resetAllFilters();
      } else getEmailLogsList(1, 10, orderByColumn, orderDirection, {
        orderId: orderId,
        Status: status,
        FromDate: fromDate,
        ToDate: toDate
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMounted, appContext.localStorageInfo.selectedProfile, triggerRefresh]);

  useEffect(() => {
    if (isMounted.current && props.search !== searchKey) {
      resetAllFilters();
      setSearchKey(props.search);
      setShowLoadingIndicator(true);
      setIsAppStartUp(true);
      if (pageIndex !== 0 || pageSize !== 10 || areFiltersmodified()) {
        if (pageIndex !== 0) setPageIndex(0);
        if (pageSize !== 10) setPageSize(10);
        if (areFiltersmodified()) resetAllFilters();
      } else getEmailLogsList(1, 10, orderByColumn, orderDirection,  {
        orderId: orderId,
        Status: status,
        FromDate: fromDate,
        ToDate: toDate
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMounted, props.search, searchKey, resetAllFilters]);

  const sortableColumns = [
    WebhookLogsEmailTableColumnIdentifiers.OrderId,
  ];
  //Handle Event for sorting
  const handleColumnSorted = useCallback(
    (column) => {
      const columnSortOption = columnFilterSort[column.identifier];
      if (sortableColumns.includes(column.identifier)) return;
      if (!columnSortOption || !columnSortOption.isSorted) {
        setColumnFilterSort((prevSortOptions) => ({
          ...prevSortOptions,
          [column.identifier]: {
            isSorted: true,
            isSortedDesc: false,
          },
        }));
        setOrderDirection("asc");
        setOrderByColumn(column.identifier);
        getEmailLogsList(pageIndex + 1, pageSize, column.identifier, "asc", {
          orderId: orderId,
          Status: status,
          FromDate: fromDate,
          ToDate: toDate
        }
        );
      } else if (columnSortOption.isSorted && !columnSortOption.isSortedDesc) {
        setColumnFilterSort((prevSortOptions) => ({
          ...prevSortOptions,
          [column.identifier]: {
            isSorted: true,
            isSortedDesc: true,
          },
        }));
        setOrderDirection("desc");
        setOrderByColumn(column.identifier);
        getEmailLogsList(pageIndex + 1, pageSize, column.identifier, "desc", {
          orderId: orderId,
          Status: status,
          FromDate: fromDate,
          ToDate: toDate
      });
      } else {
        setColumnFilterSort((prevSortOptions) => ({
          ...prevSortOptions,
          [column.identifier]: {
            isSorted: false,
            isSortedDesc: false,
          },
        }));
        setOrderDirection("");
        setOrderByColumn("");
        getEmailLogsList(pageIndex + 1, pageSize, "", "", {
          orderId: orderId,
          Status: status,
          FromDate: fromDate,
          ToDate: toDate
      });
      }
    },
    [columnFilterSort, getEmailLogsList, pageIndex, pageSize, status, fromDate, toDate]
  );

  return showLoadingIndicator ? (
    <LoadingIndicator white />
  ) : (
    <ReactTable
      titleComponent={getTitleComponent(intl.formatMessage({ id: "webhookLogs.email.table.title" }))}
      identifier={TableIdentifiers.TradingPartner}
      data={emailLogsDetails}
      columns={columns}
      showExport={true}
      useColumnFilters
      onRefresh={toggleRefresh}
      hideSearch={true}
      queryCount={count}
      queryPageSize={pageSize}
      queryPageIndex={pageIndex}
      handlePageChange={handlePageChange}
      handlePageSizeChange={handlePageSizeChange}
      handleResetFilterChange={resetAllFilters}
      autoResetFilters={false}
      handleColumnSorted={handleColumnSorted}
      columnSortOptions={columnFilterSort}
      options={WebhookEmailStatus}
      handleSearch={handleSearch}
      hasCustomFilters={true}
      showCopy={false}
      showPrint={false}
    />
  );
}


const getTitleComponent = (title) => {
  return (
    <div className="flex-fill">
      <h3 className="mb-2">{title}</h3>
    </div>
  );
};

const getColumns = (
  intl: IntlShape,
  toggleRefresh,
) => [
    {
      Header: intl.formatMessage({ id: "webhookLogs.table.orderId" }),
      accessor: "orderId",
      identifier: WebhookLogsEmailTableColumnIdentifiers.OrderId,
      Cell: (cell: any) => getValueOrEmpty(cell.cell.row.original.orderId)
    },
    {
      Header: intl.formatMessage({ id: "webhookLogs.table.emailAddress" }),
      accessor: "recipient",
      identifier: WebhookLogsEmailTableColumnIdentifiers.Recipient,
      Cell: (cell: any) => getValueOrEmpty(cell.cell.row.original.recipient)
    },
    {
      Header: intl.formatMessage({ id: "webhookLogs.table.status" }),
      accessor: "deliveryStatus",
      identifier: WebhookLogsEmailTableColumnIdentifiers.DeliveryStatus,
      Cell: (cell: any) => getValueOrEmpty(cell.cell.row.original.deliveryStatus)
    },
    {
      Header: intl.formatMessage({ id: "webhookLogs.table.creationDate" }),
      accessor: "dateTime",
      identifier: WebhookLogsEmailTableColumnIdentifiers.CreationDate,
      Cell: (cell: any) => ( cell.cell.row.original.dateTime ? <FormatDateTime utcDate={cell.cell.row.original.dateTime}/> : "")
    }
  ];
